import { useContext, useEffect, useRef, useState } from 'react'
import useDebounce from '../hooks/useDebounce'
import ElectronIPC from '../services/ElectronIPC'
import { logger } from '../services/Logger'
import { GlobalOutputDeviceType, UserContext, UserContextType } from '../UserContext'
import useGlobalOutputDeviceAction from '../actions/useGlobalOutputDeviceActions'
import useUserContextRef from '../services/useUserContextRef'
import useAppContextRef from '../services/useAppContextRef'
import { ExternalEventEmitter } from '../ExternalEventEmitter'
import { ExternalEvent } from '../AppContext'

const log = logger.createChildLogger({ name: 'GlobalOutputDeviceService' })

function useGlobalOutputDeviceService() {
    const { state, dispatch } = useContext(UserContext) as UserContextType

    const { appStateRef } = useAppContextRef()

    const { userStateRef } = useUserContextRef()

    const { revertToPreviousGlobalAudioOutputDevice } = useGlobalOutputDeviceAction()

    const [_outputDevice, set_outputDevice] = useState<GlobalOutputDeviceType | null>(null)
    const outputDevice_debounced = useDebounce(_outputDevice, 1000, { ignoreInitialUpdate: true })

    const isDisabled = state.clientType === 'listenonly'

    useEffect(() => {
        if (isDisabled) {
            return
        }

        setTimeout(() => {
            log.verbose('load', `call ElectronIPC.triggerGlOdevChanged()`)
            ElectronIPC.triggerGlOdevChanged()
        }, 2500)

        return () => {
            revertToPreviousGlobalAudioOutputDevice()
        }
    }, [])

    useEffect(() => {
        const handler = (externalEvent: ExternalEvent) => {
            if (externalEvent.eventName === 'gl-odev-changed') {
                const isDisabledRef = userStateRef.current.clientType === 'listenonly'
                if (isDisabledRef) {
                    return
                }

                const outputDevice = externalEvent.value as GlobalOutputDeviceType
                log.verbose('externalEvent', `got event gl-odev-changed: ` + JSON.stringify(externalEvent.value))

                if (JSON.stringify(outputDevice) !== JSON.stringify(_outputDevice)) {
                    log.verbose('externalEvent', `calling set_outputDevice().`)
                    set_outputDevice(outputDevice)
                } else {
                    log.verbose('externalEvent', `no-op due to no changes.`)
                }
            }
        }

        ExternalEventEmitter.on('event', handler)

        return () => {
            ExternalEventEmitter.removeListener('event', handler)
        }
    }, [])

    useEffect(() => {
        if (isDisabled) {
            return
        }
        if (outputDevice_debounced == null) {
            return
        }

        const oDev = outputDevice_debounced
        const isFirstRun = userStateRef.current.mediaSelection.globalOutputDeviceHistory.length === 0

        log.verbose(
            'outputDevice_debounced',
            `GlobalOutputDeviceManager: dispatching 'global_output_device_changed' (debouncing applied): ` +
                JSON.stringify(oDev)
        )

        // NOTE: `audioOutputs` does not contain system audio devices which is intended.
        const { audioOutputs } = userStateRef.current.mediaSources
        if (isFirstRun) {
            let outputDevice = audioOutputs.find((a) => a.nativeDeviceId === oDev.nativeDeviceId)
            if (!outputDevice) {
                outputDevice = audioOutputs.find((a) => a.deviceUid === oDev.uid)

                if (!outputDevice) {
                    outputDevice = audioOutputs.find((a) => a.label === oDev.label)
                }
            }

            if (outputDevice && appStateRef.current.appSettings.selectSystemDefaultForAudioOutputOnStartup) {
                dispatch({
                    type: 'update_media_selection',
                    value: {
                        audioOutput: outputDevice.deviceId,
                    },
                })
            }
        }

        dispatch({
            type: 'global_output_device_changed',
            value: oDev,
        })
    }, [outputDevice_debounced])

    // revert previous global audio output device selection if system audio device deselected
    const prevSelectedMuseAudioShareCt = useRef(0)
    useEffect(
        function revertToPreviousGlobalAudioOutputDeviceOnMuseAudioShareDeselected() {
            if (isDisabled) {
                return
            }

            const scope = `revertToPreviousGlobalAudioOutputDeviceOnMuseAudioShareDeselected`
            // detect condition where there are no system audio selected
            const audioInputs = state.mediaStreams.audioInputs
            const selectedMuseAudioShareCt = audioInputs.filter((a) => a.info.isMuseAudioShare).length
            log.verbose(scope, `selectedMuseAudioShareCt=${selectedMuseAudioShareCt}.`)

            // when `selectedMuseAudioShareCt `goes from non-zero to zero, that's when we
            // attempt to reset the global audio output device to the previous device.
            if (prevSelectedMuseAudioShareCt.current > 0 && selectedMuseAudioShareCt === 0) {
                revertToPreviousGlobalAudioOutputDevice()
            }

            prevSelectedMuseAudioShareCt.current = selectedMuseAudioShareCt
        },
        [state.mediaStreams.audioInputs]
    )

    return <></>
}

export default useGlobalOutputDeviceService
